<template>
  <v-main>
    <v-container fluid>
      <!-- Professional Form Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3" class="form-card">
            <!-- Page Header integrated into Form Card -->
            <v-card-title class="page-header py-2 px-3">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Title and Description -->
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">Edit Customer</h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        Update patient account information and details
                      </p>
                    </div>
                  </div>
                </v-col>

                <!-- Right Side: Back Button and Form Badge -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip color="info" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-account-edit</v-icon>
                      Customer Update
                    </v-chip>
                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="$router.back()"
                      class="back-button"
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back to Customers
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider></v-divider>

            <!-- Loading State -->
            <div v-if="isLoading" class="text-center pa-8">
              <health-loader message="Loading patient information..." />
            </div>

            <!-- Form Content -->
            <v-card-text v-else class="form-content px-2 py-2">
              <v-form ref="form" v-model="valid" lazy-validation>
                <!-- Basic Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-account</v-icon>
                    Basic Information
                  </h3>

                  <v-row>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="form.first_name"
                        label="First Name *"
                        placeholder="Enter first name"
                        outlined
                        :rules="[(v) => !!v || 'First name is required']"
                        :error-messages="errors.first_name"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-account</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="form.middle_name"
                        label="Middle Name (Optional)"
                        placeholder="Enter middle name"
                        outlined
                        :error-messages="errors.middle_name"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-account</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="form.last_name"
                        label="Last Name *"
                        placeholder="Enter last name"
                        outlined
                        :rules="[(v) => !!v || 'Last name is required']"
                        :error-messages="errors.last_name"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-account</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="form.user_name"
                        label="Username"
                        placeholder="Enter username (optional)"
                        outlined
                        :error-messages="errors.user_name"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-account-circle</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-select
                        v-model="form.gender_id"
                        :items="genderItems"
                        label="Gender"
                        placeholder="Select gender"
                        outlined
                        :error-messages="errors.gender_id"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-human-male-female</v-icon>
                        </template>
                      </v-select>
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-menu
                        v-model="dobMenu"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        max-width="290px"
                        min-width="auto"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="form.dob"
                            label="Date of Birth"
                            placeholder="YYYY-MM-DD"
                            outlined
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            :error-messages="errors.dob"
                            class="professional-field"
                            hide-details="auto"
                          >
                            <template v-slot:prepend-inner>
                              <v-icon color="grey">mdi-calendar</v-icon>
                            </template>
                          </v-text-field>
                        </template>
                        <v-date-picker
                          v-model="form.dob"
                          :max="new Date().toISOString().substr(0, 10)"
                          @input="dobMenu = false"
                        ></v-date-picker>
                      </v-menu>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="form.bloodgroup_id"
                        :items="bloodGroupItems"
                        label="Blood Group"
                        placeholder="Select blood group"
                        outlined
                        :error-messages="errors.bloodgroup_id"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-water</v-icon>
                        </template>
                      </v-select>
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="form.weight"
                        label="Weight (kg)"
                        placeholder="Enter weight"
                        type="number"
                        step="0.01"
                        outlined
                        :error-messages="errors.weight"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-scale-bathroom</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="form.height"
                        label="Height (cm)"
                        placeholder="Enter height"
                        type="number"
                        step="0.01"
                        outlined
                        :error-messages="errors.height"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-human-male-height</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12">
                      <v-file-input
                        v-model="form.image_file"
                        label="Profile Image"
                        placeholder="Select profile image"
                        outlined
                        accept="image/*"
                        prepend-icon=""
                        :error-messages="errors.image_path"
                        class="professional-field"
                        hide-details="auto"
                        @change="onImageSelect"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-camera</v-icon>
                        </template>
                      </v-file-input>
                      <div v-if="form.image_preview || patient.image_path" class="mt-2">
                        <v-img
                          :src="form.image_preview || getImageUrl(patient.image_path)"
                          max-width="150"
                          max-height="150"
                          class="rounded"
                        ></v-img>
                      </div>
                    </v-col>
                  </v-row>
                </div>

                <!-- Contact Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-phone</v-icon>
                    Contact Information
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.email"
                        label="Email Address"
                        placeholder="Enter email address"
                        outlined
                        type="email"
                        :rules="[
                          v => !v || /.+@.+\..+/.test(v) || 'Email must be valid if provided',
                        ]"
                        @input="errors.email = ''"
                        :error-messages="errors.email"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-email</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.phone"
                        label="Phone Number *"
                        placeholder="Enter 10-digit phone number"
                        outlined
                        :rules="[
                          (v) => !!v || 'Phone number is required',
                          (v) => /^\d{10}$/.test(v) || 'Phone number must be exactly 10 digits',
                        ]"
                        :error-messages="errors.phone"
                        class="professional-field"
                        hide-details="auto"
                        maxlength="10"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-phone</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.imei_number"
                        label="IMEI Number"
                        placeholder="Enter IMEI number"
                        outlined
                        :error-messages="errors.imei_number"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-cellphone</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.guest_code"
                        label="Guest Code"
                        placeholder="Patient ID/Guest Code"
                        outlined
                        :error-messages="errors.guest_code"
                        class="professional-field"
                        hide-details="auto"
                        readonly
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-identifier</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <!-- Address Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-home</v-icon>
                    Address Information
                  </h3>

                  <v-row>
                    <v-col cols="12">
                      <v-textarea
                        v-model="form.address"
                        label="Full Address"
                        placeholder="Enter complete address with landmark"
                        outlined
                        rows="3"
                        :error-messages="errors.address"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-home</v-icon>
                        </template>
                      </v-textarea>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="3">
                      <v-autocomplete
                        v-model="form.state"
                        :items="stateItems"
                        label="State"
                        placeholder="Select state"
                        outlined
                        @change="onStateChange"
                        :loading="statesLoading"
                        :error-messages="errors.state"
                        class="professional-field"
                        hide-details="auto"
                        :menu-props="{ maxHeight: 420 }"
                        no-data-text="No states found"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-map</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>

                    <v-col cols="12" md="3">
                      <v-combobox
                        v-model="form.city"
                        :items="cityItems"
                        label="City"
                        placeholder="Select or type city"
                        outlined
                        :disabled="!form.state"
                        :loading="citiesLoading"
                        :error-messages="errors.city"
                        class="professional-field"
                        hide-details="auto"
                        :menu-props="{ maxHeight: 420 }"
                        clearable
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-city</v-icon>
                        </template>
                      </v-combobox>
                    </v-col>

                    <v-col cols="12" md="3">
                      <v-text-field
                        v-model="form.locality"
                        label="Locality"
                        placeholder="Enter locality/area"
                        outlined
                        :error-messages="errors.locality"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-map-marker</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="3">
                      <v-text-field
                        v-model="form.pincode"
                        label="Pincode"
                        placeholder="Enter 6-digit pincode"
                        type="text"
                        inputmode="numeric"
                        maxlength="6"
                        outlined
                        :rules="[
                          v => !v || /^\d{6}$/.test(String(v)) || 'Pincode must be exactly 6 digits'
                        ]"
                        :error-messages="errors.pincode"
                        class="professional-field"
                        hide-details="auto"
                        @keypress="v => /\d/.test(v.key) || v.preventDefault()"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-mailbox</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <!-- Account Settings Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-cog</v-icon>
                    Account Settings
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <div class="d-flex align-center">
                        <v-switch
                          v-model="form.is_active"
                          label="Active Status"
                          color="success"
                          hide-details
                          class="mr-3"
                        ></v-switch>
                        <status-chip :color="form.is_active ? 'success' : 'error'" :label="String(form.is_active ? 'Active' : 'Inactive')" />
                      </div>
                      <div class="text-caption grey--text mt-1">
                        {{ form.is_active ? 'Patient account is active and can login' : 'Patient account is inactive and cannot login' }}
                      </div>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.wallet"
                        label="Wallet Balance"
                        placeholder="Enter wallet balance"
                        type="number"
                        step="0.01"
                        outlined
                        :error-messages="errors.wallet"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-wallet</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </div>

              </v-form>
            </v-card-text>

            <!-- Form Actions Footer -->
            <v-divider></v-divider>
            <v-card-actions class="form-actions pa-2">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <v-chip
                      small
                      :color="hasValidationErrors() ? 'error' : 'success'"
                      :text-color="hasValidationErrors() ? 'white' : 'white'"
                      class="mr-3"
                    >
                      <v-icon left x-small>
                        {{
                          hasValidationErrors()
                            ? "mdi-alert-circle"
                            : "mdi-check-circle"
                        }}
                      </v-icon>
                      {{
                        hasValidationErrors()
                          ? "Please fix validation errors"
                          : "Form validation passed"
                      }}
                    </v-chip>
                    <span class="text-caption grey--text">
                      All required fields must be completed
                    </span>
                  </div>
                </v-col>

                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex justify-end">
                    <v-btn
                      color="grey"
                      outlined
                      class="mr-3 action-button"
                      @click="$router.back()"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                      color="primary"
                      class="action-button"
                      @click="updatePatient"
                      :loading="isUpdating"
                      :disabled="isUpdating || !valid"
                    >
                      <v-icon left>mdi-content-save</v-icon>
                      Update Patient
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "PatientEditPage",
  data() {
    return {
      valid: false,
      isLoading: true,
      isUpdating: false,
      dobMenu: false,
      patient: {},
      form: {
        first_name: "",
        middle_name: "",
        last_name: "",
        user_name: "",
        email: "",
        phone: "",
        imei_number: "",
        guest_code: "",
        gender_id: null,
        bloodgroup_id: null,
        dob: "",
        weight: "",
        height: "",
        address: "",
        city: "",
        state: "",
        locality: "",
        pincode: "",
        wallet: 0,
        is_active: true,
        image_file: null,
        image_preview: null,
      },
      errors: {},
      statesLoading: false,
      citiesLoading: false,
      stateItems: [],
      cityItems: [],
      genderItems: [],
      bloodGroupItems: [],
    };
  },
  computed: {
    patientId() {
      return this.$route.params.id;
    },
  },
  mounted() {
    this.loadPatient();
    this.getMasters();
    this.loadStates();
  },
  methods: {
    async loadPatient() {
      try {
        this.isLoading = true;
        const response = await axios.get(`users/${this.patientId}`);
        this.patient = response.data.data;
        
        // Populate form with patient data
        this.form = {
          ...this.form,
          first_name: this.patient.first_name || "",
          middle_name: this.patient.middle_name || "",
          last_name: this.patient.last_name || "",
          user_name: this.patient.user_name || "",
          email: this.patient.email || "",
          phone: this.patient.phone || "",
          imei_number: this.patient.imei_number || "",
          guest_code: this.patient.guest_code || "",
          gender_id: this.patient.gender_id || null,
          bloodgroup_id: this.patient.bloodgroup_id || null,
          dob: this.patient.dob || "",
          weight: this.patient.weight || "",
          height: this.patient.height || "",
          address: this.patient.address || "",
          city: this.patient.city || "",
          state: this.patient.state || "",
          locality: this.patient.locality || "",
          pincode: this.patient.pincode || "",
          wallet: this.patient.wallet || 0,
          is_active: this.patient.is_active === undefined ? true : !!this.patient.is_active,
        };

        // Load cities for the existing state without clearing the city value
        if (this.form.state) {
          const existingCity = this.form.city;
          await this.loadCitiesForState(this.form.state);
          this.form.city = existingCity;
          if (existingCity && !this.cityItems.find(c => c.value === existingCity)) {
            this.cityItems.unshift({ text: existingCity, value: existingCity });
          }
        }
        
      } catch (error) {
        console.error("Error loading patient:", error);
        this.$toast.error('Failed to load patient information');
        this.$router.back();
      } finally {
        this.isLoading = false;
      }
    },

    async getMasters() {
      try {
        const response = await axios.get(`masters?context=patient`);
        const masters = response.data;

        // Gender options
        if (masters.genders) {
          this.genderItems = masters.genders.map(gender => ({
            text: gender.description,
            value: gender.id
          }));
        }

        // Blood group options (check both possible keys)
        const bloodGroups = masters.blood_groups || masters.bloodgroups;
        if (bloodGroups) {
          this.bloodGroupItems = bloodGroups.map(bloodgroup => ({
            text: bloodgroup.description,
            value: bloodgroup.id
          }));
        }
      } catch (error) {
        console.log(error);
      }
    },

    async loadStates() {
      this.statesLoading = true;
      const fallbackStates = [
        'Andaman and Nicobar Islands','Andhra Pradesh','Arunachal Pradesh','Assam','Bihar',
        'Chandigarh','Chhattisgarh','Dadra and Nagar Haveli and Daman and Diu','Delhi','Goa',
        'Gujarat','Haryana','Himachal Pradesh','Jammu and Kashmir','Jharkhand','Karnataka',
        'Kerala','Ladakh','Lakshadweep','Madhya Pradesh','Maharashtra','Manipur','Meghalaya',
        'Mizoram','Nagaland','Odisha','Puducherry','Punjab','Rajasthan','Sikkim','Tamil Nadu',
        'Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal'
      ];
      try {
        const res = await axios.get('masters/states-list');
        const apiStates = Array.isArray(res.data.data) ? res.data.data : [];

        const seen = new Map();
        for (const s of apiStates) seen.set(s.name, s.iso2);
        for (const name of fallbackStates) if (!seen.has(name)) seen.set(name, null);

        this.stateItems = Array.from(seen, ([name, iso2]) => ({ text: name, value: name, iso2 }))
          .sort((a, b) => a.text.localeCompare(b.text));
      } catch (error) {
        console.warn('State API failed, using local list:', error);
        this.stateItems = fallbackStates
          .slice()
          .sort((a, b) => a.localeCompare(b))
          .map(name => ({ text: name, value: name, iso2: null }));
      } finally {
        this.statesLoading = false;
      }
    },

    async onStateChange() {
      this.form.city = "";
      this.cityItems = [];
      if (!this.form.state) return;
      await this.loadCitiesForState(this.form.state);
    },

    async loadCitiesForState(stateName) {
      this.citiesLoading = true;
      try {
        const selectedState = this.stateItems.find(s => s.value === stateName);

        // Primary: backend proxy (cached, no external rate limits)
        if (selectedState && selectedState.iso2) {
          const res = await axios.get(`masters/cities-by-iso?iso2=${selectedState.iso2}`);
          const cities = Array.isArray(res.data.data) ? res.data.data : [];
          if (cities.length) {
            this.cityItems = cities.map(name => ({ text: name, value: name }));
            return;
          }
        }

        // Fallback: countriesnow.space (no key required)
        const fallback = await fetch('https://countriesnow.space/api/v0.1/countries/state/cities', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ country: 'India', state: stateName }),
        });
        if (fallback.ok) {
          const json = await fallback.json();
          const list = Array.isArray(json?.data) ? json.data : [];
          this.cityItems = list
            .map(name => ({ text: String(name).trim(), value: String(name).trim() }))
            .filter(c => c.value)
            .sort((a, b) => a.text.localeCompare(b.text));
        } else {
          this.cityItems = [];
        }
      } catch (error) {
        console.warn('Cities API failed:', error);
        this.cityItems = [];
      } finally {
        this.citiesLoading = false;
      }
    },

    onImageSelect(file) {
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.form.image_preview = e.target.result;
        };
        reader.readAsDataURL(file);
      } else {
        this.form.image_preview = null;
      }
    },

    hasValidationErrors() {
      return Object.keys(this.errors).length > 0 || !this.valid;
    },

    getImageUrl(imagePath) {
      if (!imagePath) return "";
      return `${this.mediaUrl}/${imagePath}`;
    },

    async updatePatient() {
      if (!this.$refs.form.validate()) {
        return;
      }

      try {
        this.isUpdating = true;
        this.errors = {};

        // Create FormData for potential file uploads
        const formData = new FormData();

        // Add form fields
        Object.keys(this.form).forEach((key) => {
          if (key !== 'image_preview' && this.form[key] !== null && this.form[key] !== undefined) {
            formData.append(key, this.form[key]);
          }
        });

        // Add _method field for Laravel method spoofing (required for PATCH with FormData)
        formData.append('_method', 'PATCH');

        // Use POST method with _method field for update (Laravel convention with FormData)
        await axios.post(`users/${this.patientId}`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        this.isUpdating = false;
        this.$toast.success('Patient information updated successfully');
        this.$router.push('/customers');
      } catch (error) {
        this.isUpdating = false;
        if (error.response && error.response.data.errors) {
          this.errors = error.response.data.errors;
        } else {
          console.error("Error updating patient:", error);
          this.$toast.error('Failed to update patient information');
        }
      }
    },
  },
};
</script>

<style scoped>
/* Professional Form Card with Integrated Header */
.form-card {
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  border-left: 4px solid var(--v-primary-base);
}

.page-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
}

.page-title {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.1;
  margin-bottom: 0.15rem;
}

.page-subtitle {
  font-size: 0.75rem;
  line-height: 1.2;
}

.back-button {
  min-width: 100px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  font-size: 0.75rem;
}

.form-content {
  background: #ffffff;
}

/* Section Styling */
.form-section {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 0.5rem;
}

.form-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--v-primary-base);
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

/* Professional Form Fields */
.professional-field >>> .v-input__control {
  min-height: 40px;
}

.professional-field >>> .v-text-field__details {
  padding-top: 4px;
}

.professional-field >>> .v-input__slot {
  border-radius: 4px;
}

.professional-field >>> .v-text-field input {
  font-size: 0.75rem;
  padding-top: 8px;
  padding-bottom: 8px;
}

.professional-field >>> .v-label {
  font-size: 0.75rem;
  font-weight: 500;
}

.professional-field >>> .v-icon {
  color: #6c757d !important;
}

/* Form Actions */
.form-actions {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-top: 1px solid #dee2e6;
}

.action-button {
  min-width: 90px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  border-radius: 4px;
  font-size: 0.75rem;
}

/* Spacing and Alignment */
.v-row {
  margin-left: -6px;
  margin-right: -6px;
}

.v-row > .v-col {
  padding: 0 6px;
}

/* Professional Color Scheme */
.v-card {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08) !important;
}

.v-btn.v-btn--outlined {
  border: 2px solid currentColor;
}

.v-chip {
  font-weight: 500;
}

/* Clean Focus States */
.professional-field >>> .v-input--is-focused .v-input__slot {
  border-color: var(--v-primary-base) !important;
  border-width: 2px !important;
}

/* Professional Transitions */
.v-card,
.v-btn,
.professional-field {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
</style>